import React, { useCallback, useMemo } from "react";

import Big from "big.js";
import styled from "styled-components/macro";
import { v4 as uuidv4 } from "uuid";

import { useSelector } from "react-redux";

import { Button, SecondaryButton } from "components/Form";
import { Column } from "components/Layout";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { SmallText } from "components/Text";

import { Tooltip } from "components/Form/FormikControls/Tooltip";
import {
  getSideLoadedBusinessById,
  getSideLoadedBusinessMappableFieldsById,
  getSideLoadedDataMappingEntriesByRecordId,
  selectBusinessIdByAgentCodeLookup,
  selectBusinessIdBySaleyardCodeLookup,
} from "selectors";
import { Form, Formik, useFormikContext } from "formik";
import { TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";

import {
  fieldConfigurations,
  getSideLoadedFieldConfigurationsForUser,
  FieldConfiguration,
} from "./fieldConfigurations";
import { buildCompareModalInitialValues } from "lib/sideLoadedFieldMapping";
import { useUserRoleContentTypeName } from "../useSideLoadedFieldConfigurations";
import {
  FieldTable,
  SideLoadedFieldRows,
  UnmappedFieldGroups,
} from "./SideLoadedFieldRows";
import { CommissionTypes } from "constants/commissions";
import {
  parseLabelNames,
  parsePicValues,
} from "constants/sideLoadedBusinesses";
import {
  businessRelations,
  businessRelationsRateTypes,
} from "constants/businesses";
import { buildTransitInsuranceFieldsFromSideLoadedBusinessValues } from "lib/insuranceRates";
import { buildSideLoadedValidationSchema } from "./validationSchema";

// Button carries flex: 1 so the footer splits evenly between the actions.
// Tooltip's wrapper becomes the flex child in its place, so the flex has to
// move out to it or the button collapses to its label width.
const SubmitAction = styled.div`
  flex: 1;
  margin: 0 ${({ theme }) => theme.space[1]}px;
`;

interface SideLoadedFieldCompareBodyProps {
  onClose: () => void;
  recordId: string;
  businessId?: string;
  mappedConfigurations: FieldConfiguration[];
  unmappedConfigurations: FieldConfiguration[];
}

const SideLoadedFieldCompareBody = ({
  onClose,
  recordId,
  businessId,
  mappedConfigurations,
  unmappedConfigurations,
}: SideLoadedFieldCompareBodyProps) => {
  const { errors, isValid } =
    useFormikContext<SideLoadedFieldCompareModalValues>();

  const validationMessages = useMemo(
    () =>
      [...mappedConfigurations, ...unmappedConfigurations]
        .map(configuration => ({
          key: configuration.value,
          message: errors[`Value__${configuration.value}`],
        }))
        .filter(({ message }) => Boolean(message)),
    [errors, mappedConfigurations, unmappedConfigurations],
  );

  const submitTooltip = isValid ? (
    ""
  ) : (
    <span>
      Can&apos;t use this data yet:
      {validationMessages.length > 0 ? (
        validationMessages.map(({ key, message }) => (
          <React.Fragment key={key}>
            <br />
            {message}
          </React.Fragment>
        ))
      ) : (
        <>
          <br />
          Some of the selected data isn&apos;t valid.
        </>
      )}
    </span>
  );

  const sideLoadedBusiness = useSelector(getSideLoadedBusinessById(recordId));

  return (
    <>
      <DialogTitle onClose={onClose}>
        Data Suggestions for &quot;{sideLoadedBusiness.name}&quot;
      </DialogTitle>
      <DialogContent dividers>
        <Column full>
          <SmallText>
            Choose the data you&apos;d like to use from the &quot;
            {sideLoadedBusiness.name}&quot; record in &quot;
            {sideLoadedBusiness.datasetName}&quot; .
          </SmallText>

          <FieldTable size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  AgriNous Field
                  <Tooltip title="Where in AgriNous you will be mapping data to." />
                  <br />
                  <i>Current Data</i>
                </TableCell>
                <TableCell>
                  Source Field{" "}
                  <Tooltip title="The field in your data source to match to the AgriNous Field." />
                  <br />
                  <i>New Data</i>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SideLoadedFieldRows
                configurations={mappedConfigurations}
                businessId={businessId}
                recordId={recordId}
              />
            </TableBody>
          </FieldTable>
          <UnmappedFieldGroups
            configurations={unmappedConfigurations}
            businessId={businessId}
            recordId={recordId}
            defaultToOpen={mappedConfigurations.length === 0}
          />
        </Column>
      </DialogContent>
      <DialogActions>
        <SecondaryButton type="button" onClick={onClose}>
          Cancel
        </SecondaryButton>
        <SubmitAction>
          <Tooltip title={submitTooltip} fullWidth iconPadding={0}>
            <Button type="submit" disabled={!isValid} fullWidth margin="0">
              Use Data
            </Button>
          </Tooltip>
        </SubmitAction>
      </DialogActions>
    </>
  );
};

interface SideLoadedContact {
  id: number;
  index: number;
  isComplianceReportRecipient: boolean;
  isCommercialReportRecipient: boolean;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

interface SideLoadedProperty {
  id: string;
  PIC: string;
  name?: string;
  isShown: boolean;
}

export interface SideLoadedSelectValues {
  recordId: string;
  fields: Record<string, string>;
  contacts: SideLoadedContact[];
  properties: SideLoadedProperty[];
  vendorCommissions: Partial<VendorCommissionBand>[];
  relationships: BusinessRelation[];
  notes: string | null;
  labelNames: string[];
}
interface SideLoadedFieldCompareModalProps {
  onClose: () => void;
  onSelect: (selectedValues: SideLoadedSelectValues) => void;
  businessId?: string;
  recordId: string;
}

interface SideLoadedFieldCompareModalValues {
  [key: string]: string;
}

const SideLoadedFieldCompareModal = ({
  onClose,
  onSelect,
  businessId,
  recordId,
}: SideLoadedFieldCompareModalProps): React.JSX.Element => {
  const userRoleContentTypeName = useUserRoleContentTypeName();
  const visibleFieldConfigurations = useMemo(
    () => getSideLoadedFieldConfigurationsForUser(userRoleContentTypeName),
    [userRoleContentTypeName],
  );

  const businessIdByAgentCodeLookup = useSelector(
    selectBusinessIdByAgentCodeLookup as (
      state: State,
    ) => Record<string, string>,
  );
  const businessIdBySaleyardCodeLookup = useSelector(
    selectBusinessIdBySaleyardCodeLookup as (
      state: State,
    ) => Record<string, string>,
  );

  const handleSubmit = useCallback(
    (values: SideLoadedFieldCompareModalValues) => {
      // Inject a negative base, incremented by the index in order to ensure uniqueness, for contacts.
      const idBase = Date.now() * -1;
      const populatedValues: SideLoadedSelectValues = Object.entries(
        values,
      ).reduce(
        (acc: SideLoadedSelectValues, [key, targetField], index: number) => {
          if (key.startsWith("MappableField__") && targetField !== null) {
            // The field in AgriNous.
            const mappableField = key.replace("MappableField__", "");

            // Find the configuration for this mappable field.
            const configuration = Object.values(fieldConfigurations).find(
              config => config.value === mappableField,
            );

            // The value of what they've selected (or manually fiddled with)
            const newValue = values[`Value__${mappableField}`];

            if (configuration && configuration.handleAsProperty && newValue) {
              // One property per PIC; names aren't ingested.
              parsePicValues([newValue]).forEach(pic => {
                acc.properties.push({
                  id: uuidv4(),
                  PIC: pic,
                  name: "",
                  isShown: true,
                });
              });
            } else if (
              configuration &&
              configuration.handleAsContact &&
              newValue
            ) {
              // A bit more complex.... email1, firstname1, lastname1, phone1, etc need to be tied together.
              // See if we have a contact with this index already.
              const existingContact = acc.contacts.find(
                contact => contact.index === configuration.index,
              );
              if (existingContact) {
                // If we do - update the existing contact.
                existingContact[configuration.fieldName] = newValue;
              } else {
                // If we don't - add a new contact, with just this field, and general defaults.
                acc.contacts.push({
                  id: idBase - index,
                  index: configuration.index,
                  isComplianceReportRecipient: true,
                  isCommercialReportRecipient: true,
                  [configuration.fieldName]: newValue,
                });
              }
            } else if (
              configuration &&
              configuration.value ===
                fieldConfigurations.TRANSIT_INSURANCE.value
            ) {
              Object.assign(
                acc.fields,
                buildTransitInsuranceFieldsFromSideLoadedBusinessValues(
                  newValue,
                ),
              );
            } else if (
              configuration &&
              configuration.value ===
                fieldConfigurations.VENDOR_PERCENTAGE_COMMISSION.value &&
              newValue
            ) {
              const commissionValue = Big(newValue).div(100).toString();

              // Inject a row for 'all types' in the commissions table.
              acc.vendorCommissions.push({
                id: uuidv4(),
                commissionType: CommissionTypes.Percentage,
                // Note - we're dividing by 100 here to get the percentage.
                commissionValue,
                saleType: null,
                speciesId: null,
                saleSubTypeId: null,
              });
            } else if (
              configuration &&
              [
                fieldConfigurations.STOCK_AGENT.value,
                fieldConfigurations.REBATE_AGENT.value,
              ].includes(configuration.value)
            ) {
              // Inject a row for the relationship, and it's rate value.
              const relatedToId =
                businessIdByAgentCodeLookup[newValue] ||
                businessIdBySaleyardCodeLookup[newValue];

              // Pull the stock agent percentage, if this is a stock agent field; rebate for rebate agent.
              const rateValueFieldName =
                configuration.value === fieldConfigurations.STOCK_AGENT.value
                  ? fieldConfigurations.STOCK_AGENT_PERCENTAGE_COMMISSION.value
                  : fieldConfigurations.REBATE_AGENT_PERCENTAGE_COMMISSION
                      .value;

              // Mark the relationship type as stock or rebate.
              const relationType =
                configuration.value === fieldConfigurations.STOCK_AGENT.value
                  ? businessRelations.STOCK_AGENT
                  : businessRelations.REBATE_AGENT;

              // Pull the rate value as a percentage, retaining the original decimal places, without float precision errors.
              const rateValue: string = new Big(
                values[`Value__${rateValueFieldName}`] || 0,
              )
                .div(100)
                .toString();

              acc.relationships.push({
                id: uuidv4(),
                relatedFromId: businessId,
                relatedToId,
                relationType,
                rateValue,
                rateType: businessRelationsRateTypes.PERCENTAGE,
                saleType: null,
                saleSubTypeId: null,
                speciesId: null,
              });
            } else if (configuration && configuration.handleAsLabels) {
              acc.labelNames = parseLabelNames(newValue);
            } else if (configuration && configuration.handleAsNotes) {
              acc.notes = newValue;
            } else if (
              newValue &&
              configuration.value !==
                fieldConfigurations.STOCK_AGENT_PERCENTAGE_COMMISSION.value &&
              configuration.value !==
                fieldConfigurations.REBATE_AGENT_PERCENTAGE_COMMISSION.value
            ) {
              // Fallback to a dumb-set, except for the commission fields, which are handled above.
              acc.fields[configuration.targetFieldName] = newValue;
            }
          }
          return acc;
        },
        {
          recordId,
          fields: {},
          contacts: [],
          properties: [],
          vendorCommissions: [],
          relationships: [],
          notes: null,
          labelNames: [],
        },
      );
      onSelect(populatedValues);
    },
    [
      onSelect,
      recordId,
      businessIdByAgentCodeLookup,
      businessIdBySaleyardCodeLookup,
      businessId,
    ],
  );

  const sideLoadedBusiness = useSelector(getSideLoadedBusinessById(recordId));

  const mappableFields = useSelector(
    getSideLoadedBusinessMappableFieldsById(recordId),
  );

  const mappingEntries = useSelector(
    getSideLoadedDataMappingEntriesByRecordId(recordId),
  );

  const initialValues = useMemo(
    () =>
      buildCompareModalInitialValues({
        configurations: visibleFieldConfigurations,
        sourceColumns: mappableFields,
        rawData: sideLoadedBusiness.rawData,
        entries: mappingEntries,
      }),
    [
      mappableFields,
      mappingEntries,
      sideLoadedBusiness,
      visibleFieldConfigurations,
    ],
  );

  // Deliberately partitioned off the suggested mappings rather than live form
  // state, so a row mapped inside the collapsed section doesn't jump out from
  // under the user mid-edit.
  const [mappedConfigurations, unmappedConfigurations] = useMemo(() => {
    const mapped: FieldConfiguration[] = [];
    const unmapped: FieldConfiguration[] = [];
    visibleFieldConfigurations.forEach(configuration => {
      if (initialValues[`MappableField__${configuration.value}`] === null) {
        unmapped.push(configuration);
      } else {
        mapped.push(configuration);
      }
    });
    return [mapped, unmapped];
  }, [visibleFieldConfigurations, initialValues]);

  const validationSchema = useMemo(
    () =>
      buildSideLoadedValidationSchema(visibleFieldConfigurations, {
        rawData: sideLoadedBusiness.rawData,
        businessIdByAgentCodeLookup,
        businessIdBySaleyardCodeLookup,
      }),
    [
      sideLoadedBusiness,
      businessIdByAgentCodeLookup,
      businessIdBySaleyardCodeLookup,
      visibleFieldConfigurations,
    ],
  );

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="lg" fullWidth>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
      >
        <Form>
          <SideLoadedFieldCompareBody
            mappedConfigurations={mappedConfigurations}
            unmappedConfigurations={unmappedConfigurations}
            businessId={businessId}
            recordId={recordId}
            onClose={onClose}
          />
        </Form>
      </Formik>
    </ZoomyDialog>
  );
};

export default SideLoadedFieldCompareModal;
